import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import { FaArrowLeft, FaCheckCircle, FaQuoteLeft, FaBuilding, FaIndustry } from 'react-icons/fa';
import caseStudiesData from '../content/data/caseStudies.json';

const CaseStudyDetailContainer = styled.div``;

const HeroSection = styled.div`
  padding: 6rem 0 4rem;
  background-color: var(--primary-color);
  color: white;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
  color: rgba(255, 255, 255, 0.9);
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div``;

const CaseStudyHeader = styled.div`
  margin-bottom: 2rem;
`;

const ClientInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  
  svg {
    width: 24px;
    height: 24px;
    margin-right: 0.75rem;
    color: var(--primary-color);
  }
`;

const CaseStudyDescription = styled.div`
  margin-bottom: 3rem;
  
  p {
    font-size: 1.125rem;
    line-height: 1.8;
    color: var(--text-light);
    margin-bottom: 1.5rem;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  position: relative;
  padding-bottom: 0.75rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 3px;
    background-color: var(--primary-color);
  }
`;

const ResultsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0 3rem;
`;

const ResultItem = styled.li`
  padding: 1.25rem;
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  margin-bottom: 1rem;
  display: flex;
  align-items: flex-start;
  
  svg {
    color: var(--accent-color);
    margin-right: 1rem;
    margin-top: 0.25rem;
    flex-shrink: 0;
  }
`;

const TestimonialBox = styled.div`
  background-color: var(--background-off-white);
  border-radius: var(--border-radius);
  padding: 2rem;
  margin: 2rem 0;
  position: relative;
`;

const QuoteIcon = styled.div`
  position: absolute;
  top: -20px;
  left: 2rem;
  width: 40px;
  height: 40px;
  background-color: var(--accent-color);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
`;

const Quote = styled.blockquote`
  font-size: 1.25rem;
  font-style: italic;
  margin-bottom: 1rem;
  color: var(--text-dark);
`;

const Sidebar = styled.div`
  @media (max-width: 992px) {
    order: -1;
  }
`;

const SidebarCard = styled.div`
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 2rem;
  margin-bottom: 2rem;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 40px;
    height: 3px;
    background-color: var(--primary-color);
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin-bottom: 2rem;
  color: var(--primary-color);
  font-weight: 500;
  
  svg {
    margin-right: 0.5rem;
  }
  
  &:hover {
    text-decoration: underline;
  }
`;

const CaseStudyDetail = () => {
  const { id } = useParams();
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Find the case study with the matching ID
    if (caseStudiesData && caseStudiesData.caseStudies) {
      const foundCaseStudy = caseStudiesData.caseStudies.find(cs => cs.id.toString() === id);
      
      if (foundCaseStudy) {
        setCaseStudy(foundCaseStudy);
      }
      
      setLoading(false);
    }
  }, [id]);
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!caseStudy) {
    return (
      <Section>
        <div style={{ textAlign: 'center' }}>
          <h2>Case Study Not Found</h2>
          <p>The case study you're looking for does not exist.</p>
          <Button to="/case-studies">Back to Case Studies</Button>
        </div>
      </Section>
    );
  }
  
  return (
    <CaseStudyDetailContainer>
      <HeroSection>
        <div className="container">
          <HeroTitle>{caseStudy.client}</HeroTitle>
          <HeroSubtitle>
            {caseStudy.summary}
          </HeroSubtitle>
        </div>
      </HeroSection>
      
      <Section>
        <BackLink to="/case-studies"><FaArrowLeft /> Back to Case Studies</BackLink>
        
        <ContentWrapper>
          <MainContent>
            <CaseStudyHeader>
              <ClientInfo>
                <FaBuilding />
                <span><strong>Client:</strong> {caseStudy.client}</span>
              </ClientInfo>
              <ClientInfo>
                <FaIndustry />
                <span><strong>Industry:</strong> {caseStudy.industry}</span>
              </ClientInfo>
            </CaseStudyHeader>
            
            <CaseStudyDescription>
              <SectionTitle>Challenge</SectionTitle>
              <p>{caseStudy.challenge}</p>
              
              <SectionTitle>Solution</SectionTitle>
              <p>{caseStudy.solution}</p>
              
              <SectionTitle>Results</SectionTitle>
              <ResultsList>
                {caseStudy.results.map((result, index) => (
                  <ResultItem key={index}>
                    <FaCheckCircle size={24} />
                    <span>{result}</span>
                  </ResultItem>
                ))}
              </ResultsList>
              
              {caseStudy.testimonial && (
                <TestimonialBox>
                  <QuoteIcon>
                    <FaQuoteLeft />
                  </QuoteIcon>
                  <Quote>"{caseStudy.testimonial}"</Quote>
                </TestimonialBox>
              )}
            </CaseStudyDescription>
          </MainContent>
          
          <Sidebar>
            <SidebarCard>
              <CardTitle>Discuss Your Project</CardTitle>
              <p>Interested in achieving similar results for your organization?</p>
              <Button to="/contact" fullWidth variant="primary">Contact Us</Button>
            </SidebarCard>
            
            <SidebarCard>
              <CardTitle>Related Case Studies</CardTitle>
              <ul style={{ padding: 0, listStyle: 'none' }}>
                {caseStudiesData.caseStudies
                  .filter(cs => cs.id.toString() !== id)
                  .slice(0, 3)
                  .map((relatedCase, index) => (
                    <li key={index} style={{ marginBottom: '1rem' }}>
                      <Link to={`/case-studies/${relatedCase.id}`} style={{ color: 'var(--primary-color)', fontWeight: '500' }}>
                        {relatedCase.client} - {relatedCase.industry}
                      </Link>
                    </li>
                  ))
                }
              </ul>
            </SidebarCard>
            
            <SidebarCard>
              <CardTitle>Our Services</CardTitle>
              <p>Explore our range of commodity trading solutions:</p>
              <Button to="/services" fullWidth variant="outline" style={{ marginTop: '1rem' }}>View All Services</Button>
            </SidebarCard>
          </Sidebar>
        </ContentWrapper>
      </Section>
    </CaseStudyDetailContainer>
  );
};

export default CaseStudyDetail; 